// @edemics/lib/subdomain — pure helpers for host-based tenant resolution.
//
// Tenant multi-tenancy on the platform is expressed on the URL: each tenant
// gets a subdomain of an apex (e.g. `acme.edemics.com`). The FE derives the
// active tenant from `window.location.hostname` at boot and passes it to
// the BE both as an HTTP header (see @edemics/lib/api) and — post-login —
// by redirecting to the tenant's subdomain so every subsequent request
// comes off the correct host.
//
// The two apex domains this platform serves today:
//   • edemics.com          — public marketing + tenant subdomains
//   • app.edemics.com      — app-only apex used by some tenants pre-cutover
//
// Custom-domain tenants (R4) will NOT match either apex here; they get
// resolved by a separate path (Host header → BE lookup) that this helper
// does not yet know about.
//
// Reserved subdomains are treated as "no tenant" so app/api/admin/etc
// don't get mistaken for tenants.

export const APEXES = ["edemics.com", "app.edemics.com"] as const

export const RESERVED_SUBDOMAINS = new Set<string>([
  "www",
  "app",
  "api",
  "bknd",
  "backend",
  "admin",
  "super-admin",
  "staging",
  "test",
  "dev",
  "preview",
])

/**
 * Extract the tenant subdomain from a hostname, or `null` when the host
 * is an apex, a reserved subdomain, `localhost`, or an unrelated domain.
 *
 *   getSubdomainFromHostname("acme.edemics.com")      // → "acme"
 *   getSubdomainFromHostname("edemics.com")           // → null (apex)
 *   getSubdomainFromHostname("www.edemics.com")       // → null (reserved)
 *   getSubdomainFromHostname("app.edemics.com")       // → null (apex)
 *   getSubdomainFromHostname("acme.localhost:5173")   // → null (dev)
 *   getSubdomainFromHostname("acme.example.com")      // → null (unknown apex)
 *
 * Ports are stripped, host is lower-cased. Multi-segment tenant slugs (e.g.
 * `staff.acme.edemics.com`) are returned verbatim as `staff.acme` — the
 * BE decides whether to accept them.
 */
export function getSubdomainFromHostname(hostname: string): string | null {
  if (!hostname) return null
  const host = hostname.split(":")[0].toLowerCase()

  // Dev: <sub>.localhost or plain localhost / 127.0.0.1 → no tenant.
  // The tenant-app running locally hits BE via `VITE_API_URL` and doesn't
  // rely on subdomain routing.
  if (
    host === "localhost" ||
    host.endsWith(".localhost") ||
    host === "127.0.0.1" ||
    host === "0.0.0.0"
  ) {
    return null
  }

  for (const apex of APEXES) {
    if (host === apex) return null // apex itself, no subdomain
    if (host.endsWith(`.${apex}`)) {
      const sub = host.slice(0, -1 - apex.length)
      if (!sub) return null
      if (RESERVED_SUBDOMAINS.has(sub)) return null
      return sub
    }
  }

  // Custom domain, unknown host, IP address — R4 will resolve these via BE.
  return null
}

/**
 * Convenience wrapper reading the current browser hostname. Returns `null`
 * in non-browser environments (SSR / tests) so callers don't have to guard.
 */
export function getCurrentSubdomain(): string | null {
  if (typeof window === "undefined") return null
  return getSubdomainFromHostname(window.location.hostname)
}

/**
 * Wave S (2026-09-10) — reserved platform host set. Includes the two
 * apexes (marketing + app) and the well-known reserved hosts a tenant
 * could never legitimately serve their app from. Used to decide whether
 * to send `X-Tenant-Host` on outbound API requests.
 */
const RESERVED_HOSTS = new Set<string>([
  "edemics.com",
  "www.edemics.com",
  "app.edemics.com",
  "admin.edemics.com",
  "bknd.edemics.com",
  "api.edemics.com",
  "www.test.edemics.com",
  "test.edemics.com",
])

/**
 * Wave S (2026-09-10) — is the current browser hostname a tenant host?
 *
 * A tenant host is either:
 *   - `<slug>.edemics.com` where <slug> is not reserved, OR
 *   - a custom-domain host (anything not on the reserved set and not a
 *     dev host / IP).
 *
 * Returns the normalized hostname (lower-cased, port stripped) when it
 * looks like a tenant host, else `null`. The BE has authoritative
 * knowledge — this helper is intentionally permissive so the FE sends
 * `X-Tenant-Host` for any non-platform hostname and lets the BE decide
 * whether the tenant behind it exists.
 */
export function getTenantHostname(hostname: string): string | null {
  if (!hostname) return null
  const host = hostname.split(":")[0].toLowerCase()
  if (
    host === "localhost" ||
    host.endsWith(".localhost") ||
    host === "127.0.0.1" ||
    host === "0.0.0.0"
  ) {
    return null
  }
  if (RESERVED_HOSTS.has(host)) return null
  // Reserved subdomain-of-edemics case (e.g. `preview.edemics.com`) —
  // the subdomain helper already excludes these from the tenant set,
  // so mirror that decision here.
  if (host.endsWith(".edemics.com")) {
    const slug = host.slice(0, -".edemics.com".length)
    if (RESERVED_SUBDOMAINS.has(slug)) return null
    if (!slug) return null
    return host
  }
  // Custom domain — anything else that parses as a valid hostname.
  // Very loose validation on purpose; BE is authoritative.
  if (!/^[a-z0-9][a-z0-9.-]{0,252}$/i.test(host)) return null
  return host
}

/**
 * Browser wrapper for `getTenantHostname`. Returns `null` in non-browser
 * environments (SSR / tests) so callers don't have to guard.
 */
export function getCurrentTenantHostname(): string | null {
  if (typeof window === "undefined") return null
  return getTenantHostname(window.location.hostname)
}
